import type { AvatarConfig } from './avatar-catalog'
import { avatarPieceColor, hidesAvatarBase } from './avatar-custom'

export function AvatarFace({ config: c }: { config: AvatarConfig }) {
  return (
    <>
      <g
        fill={avatarPieceColor(c, 'brows', c.hairColor)}
        visibility={hidesAvatarBase(c, 'brows') ? 'hidden' : undefined}
      >
        {[88, 152].map((x, i) => (
          <path
            key={x}
            transform={`translate(${x} 105) scale(${i ? -1 : 1} 1)`}
            d={
              c.brows === 'arched'
                ? 'M-13-4Q0-17 14-7L13-3Q0-10-12 0Z'
                : c.brows === 'straight'
                  ? 'M-13-8H13V-3H-13Z'
                  : c.brows === 'bold'
                    ? 'M-14-9Q0-13 14-7L13 0Q0-5-13-2Z'
                    : 'M-12-5Q0-11 12-5L12-2Q0-6-12-2Z'
            }
          />
        ))}
      </g>
      {!hidesAvatarBase(c, 'eyes') &&
        [88, 152].map((x, i) => {
          const closed = c.eyes === 'smile' || (c.eyes === 'wink' && i === 1)
          const ry = c.eyes === 'almond' ? 11 : c.eyes === 'bright' ? 21 : 17
          return (
            <g key={x} transform={`translate(${x} 126)`}>
              {closed ? (
                <path
                  d="M-13 2Q0-13 13 2"
                  fill="none"
                  stroke="#302E29"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              ) : (
                <>
                  <ellipse rx={c.eyes === 'bright' ? 18 : 16} ry={ry} fill="#FFFEF9" />
                  <ellipse
                    cx="2"
                    cy="1"
                    rx="8.5"
                    ry={Math.min(ry - 2, 12)}
                    fill={c.eyeColor}
                  />
                  <ellipse
                    cx="2"
                    cy="2"
                    rx="4.5"
                    ry={Math.min(ry - 4, 7.5)}
                    fill="#242925"
                  />
                  <circle cx="5" cy="-4" r="3.2" fill="#FFF" />
                  {c.eyes === 'bright' && (
                    <path
                      d="M-13-11l-6-4m10 0-3-7"
                      stroke="#302E29"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                  )}
                </>
              )}
            </g>
          )
        })}
      <path d="M117 132l-4 16q6 5 13 0" fill="#492514" opacity=".16" />
      {c.detail === 'blush' && (
        <g fill={avatarPieceColor(c, 'detail', '#E67678')} opacity=".45">
          <ellipse cx="78" cy="149" rx="13" ry="7" />
          <ellipse cx="162" cy="149" rx="13" ry="7" />
        </g>
      )}
      {c.detail === 'freckles' && (
        <g fill={avatarPieceColor(c, 'detail', '#623B27')} opacity=".48">
          {[75, 83, 92, 148, 157, 165].map((x, i) => (
            <circle key={x} cx={x} cy={148 + (i % 2) * 4} r="1.8" />
          ))}
        </g>
      )}
      {c.facialHair === 'beard' && (
        <path
          d="M68 144Q77 164 94 157Q118 148 145 157Q162 162 171 143L165 177Q152 198 120 199Q87 197 75 177Z"
          fill={avatarPieceColor(c, 'facialHair', c.hairColor)}
        />
      )}
      {c.facialHair === 'stubble' && (
        <path
          d="M74 157Q90 184 120 184Q150 184 166 157L160 179Q147 194 120 194Q93 194 80 178Z"
          fill={avatarPieceColor(c, 'facialHair', c.hairColor)}
          opacity=".3"
        />
      )}
      <g
        transform="translate(120 164)"
        visibility={hidesAvatarBase(c, 'mouth') ? 'hidden' : undefined}
      >
        {c.mouth === 'smile' && (
          <path
            d="M-15-2Q0 14 15-2"
            fill="none"
            stroke={avatarPieceColor(c, 'mouth', '#773F35')}
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}
        {c.mouth === 'grin' && (
          <>
            <path
              d="M-20-5H20Q18 17 0 17Q-18 17-20-5Z"
              fill={avatarPieceColor(c, 'mouth', '#633832')}
            />
            <path d="M-17-4H17L14 3H-14Z" fill="#FFFEF9" />
            <path d="M-8 14Q0 7 8 14Q0 18-8 14" fill="#EB8F8D" />
          </>
        )}
        {c.mouth === 'laugh' && (
          <>
            <ellipse
              cy="4"
              rx="16"
              ry="16"
              fill={avatarPieceColor(c, 'mouth', '#633832')}
            />
            <path d="M-11-7H11L9-1H-9Z" fill="#FFFEF9" />
            <ellipse cy="14" rx="9" ry="5" fill="#EB8F8D" />
          </>
        )}
        {c.mouth === 'calm' && (
          <path
            d="M-10 2Q0 6 10 2"
            fill="none"
            stroke={avatarPieceColor(c, 'mouth', '#773F35')}
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}
        {c.mouth === 'kiss' && (
          <path
            d="M-5-5Q13-6 4 1Q14 7-5 8"
            fill="none"
            stroke={avatarPieceColor(c, 'mouth', '#B05B59')}
            strokeWidth="4"
            strokeLinecap="round"
          />
        )}
      </g>
      {c.facialHair === 'moustache' && (
        <path
          d="M120 156Q108 145 97 159L88 159Q98 174 119 162Q141 174 153 159L143 159Q133 145 120 156Z"
          fill={avatarPieceColor(c, 'facialHair', c.hairColor)}
        />
      )}
    </>
  )
}
